// src/clock/index.js — Clock module public surface (v88.0)
//
// Bundled by esbuild as dist/clock.js with globalName=Clock.
// Loaded in index.html via <script src="dist/clock.js"> in <head>.
//
// Public surface (called by index.html via Clock.<name>):
//
//   STATE
//     Clock.events                    → the live-today map (rota/balance reads)
//     Clock.history                   → the per-date history cache
//     Clock.getEventForDate(id, d)    → router: live or history
//     Clock.getForWeek(id, wk, day)   → current-week summary lookup
//     Clock.loadFromDB(dateStr)       → fetch + populate state from /api/clock
//
//   ACTION
//     Clock.doAction(id, action, t)   → entry point for clock_in/out/break
//     Clock.autoAccrueToil(...)       → exported for testability; internal use
//
//   LOCATION
//     Clock.captureLocation()         → geotag + reverse geocode
//     Clock.checkLocationPermission() → silent permission probe
//     Clock.renderLocationBanner()    → "enable location" banner
//     Clock.dismissLocPrompt(event)   → session-scoped dismiss
//     Clock.requestLocationPermission() → native prompt trigger
//     Clock.retryLocationPermission() → from help modal
//     Clock.openLocHelp() / closeLocHelp() → modal lifecycle
//
//   RENDER (v88.0)
//     Clock.renderMulti()             → area-manager clock view (table per store)
//     Clock.renderSingle()            → staff clock view (card per person)
//
//   DETAIL (v88.0)
//     Clock.openDetail(staffId)       → full-screen detail sheet
//     Clock.closeDetail()             → close the sheet
//
//   STUCK (v88.0)
//     Clock.checkStuck()              → query DB for stuck past-day sessions
//     Clock.renderStuckBanner()       → paint the persistent red banner
//     Clock.openStuckModal()          → open the resolve modal (oldest first)
//     Clock.closeStuckModal()         → close the resolve modal
//     Clock.submitResolve()           → write clock_out, advance to next stuck row
//
// Renames at the module boundary (internal → external):
//   getClockEventForDate     → getEventForDate
//   getClockForWeek          → getForWeek
//   loadClockFromDB          → loadFromDB
//   doClock                  → doAction
//   clockEvents              → events
//   clockHistoryByDate       → history
//   renderMultiStoreClock    → renderMulti              (v88.0)
//   renderSingleStoreClock   → renderSingle             (v88.0)
//   openClockDetail          → openDetail               (v88.0)
//   closeClockDetail         → closeDetail              (v88.0)
//   checkStillClockedIn      → checkStuck               (v88.0)
//   renderStillClockedInBanner → renderStuckBanner      (v88.0)
//   submitStuckResolve       → submitResolve            (v88.0)
//   (openStuckModal, closeStuckModal kept as-is — already namespaced via "Stuck")

// State
export {
  clockEvents          as events,
  clockHistoryByDate   as history,
  getClockEventForDate as getEventForDate,
  getClockForWeek      as getForWeek,
  loadClockFromDB      as loadFromDB,
} from './state.js';

// Action
export {
  doClock as doAction,
  autoAccrueToil,
} from './action.js';

// Location
export {
  captureLocation,
  checkLocationPermission,
  renderLocationBanner,
  dismissLocPrompt,
  requestLocationPermission,
  retryLocationPermission,
  openLocHelp,
  closeLocHelp,
} from './location.js';

// Render (v88.0)
export {
  renderMultiStoreClock  as renderMulti,
  renderSingleStoreClock as renderSingle,
  // v132.51 — the clock screen's rota refresh compares the roster BEFORE and
  // AFTER re-reading the week, and only repaints when it actually changed (a
  // repaint that rebuilds the cards throws away a tap in flight). That
  // comparison needs this, so it has to leave the bundle. It was internal
  // until now; guard-what-you-call, or the check silently returns null
  // forever and the refresh never repaints (the v129.151 lesson).
  buildStoreShiftRoster,
} from './render.js';

// Detail (v88.0)
export {
  openClockDetail  as openDetail,
  closeClockDetail as closeDetail,
} from './detail.js';

// Stuck-session UI (v88.0)
export {
  checkStillClockedIn        as checkStuck,
  checkStuckThrottled,               // v136.8: the clock-screen draw (reads at most once a minute)
  forceNextStuckCheck,               // v136.8: the next draw reads again (rollover, clock actions)
  renderStillClockedInBanner as renderStuckBanner,
  openStuckModal,
  closeStuckModal,
  submitStuckResolve         as submitResolve,
} from './stuck.js';

// Offline write queue (v124.10) — pay-path reliability: a clock in/out that fails
// because the device is offline is queued and replayed on reconnect (idempotent upsert,
// no duplicate). Clock.initOutbox() wires the replay triggers; call it once on boot.
export {
  initOutbox,
  drain          as drainOutbox,
  pendingCount   as outboxPending,
  dismissDead,                       // v125.71: the banner's "✓ I've re-entered it"
} from './outbox.js';
